import type { ApiProblem } from "@vienna/contracts"

export class ApiError extends Error {
  constructor(public readonly problem: ApiProblem) {
    super(problem.title)
    this.name = "ApiError"
  }
  get status() {
    return this.problem.status
  }
  get code() {
    return this.problem.code
  }
}

/** Same-origin API client. Cookies carry the session; an idempotency key protects mutations that may be retried. */
export async function api<T>(path: string, init: RequestInit & { idempotencyKey?: string } = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json")
  if (init.idempotencyKey) headers.set("idempotency-key", init.idempotencyKey)
  const response = await fetch(`/api${path}`, { ...init, headers, credentials: "same-origin" })
  if (response.status === 204) return undefined as T
  const contentType = response.headers.get("content-type") ?? ""
  const body: unknown = contentType.includes("json") ? await response.json() : await response.text()
  if (!response.ok) {
    const isProblem = typeof body === "object" && body !== null && typeof (body as { title?: unknown }).title === "string" && typeof (body as { status?: unknown }).status === "number"
    // A gateway answer (Railway's 502 while a deploy switches over) is JSON without a title; name it plainly.
    const gateway = response.status === 502 || response.status === 503 || response.status === 504
    const fallbackTitle = gateway ? "The service is restarting. Try again in a moment." : ((body as { message?: unknown } | null)?.message as string | undefined) || response.statusText || "Request failed"
    const problem: ApiProblem = isProblem ? (body as ApiProblem) : { type: "about:blank", title: fallbackTitle, status: response.status }
    throw new ApiError(problem)
  }
  return body as T
}
